import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

/**
 * Closes open dropdown panels (MatSelect, MatAutocomplete, PrimeNG) when the user
 * scrolls any scrollable ancestor. Nested overflow containers do not notify CDK's
 * default scroll strategies, so panels otherwise stay floating on screen.
 */
@Injectable({ providedIn: 'root' })
export class CloseDropdownsOnScrollService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private started = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  init(): void {
    if (this.started || typeof this.document?.addEventListener !== 'function') {
      return;
    }
    this.started = true;

    fromEvent(this.document, 'scroll', { capture: true, passive: true })
      .pipe(throttleTime(50, undefined, { leading: true, trailing: true }), takeUntil(this.destroy$))
      .subscribe((event) => this.onScroll(event));

    fromEvent(this.document, 'wheel', { capture: true, passive: true })
      .pipe(throttleTime(50, undefined, { leading: true, trailing: true }), takeUntil(this.destroy$))
      .subscribe((event) => this.onScroll(event));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onScroll(event: Event): void {
    const target = event.target;
    if (target instanceof Element && this.isInsideDropdownPanel(target)) {
      return;
    }
    this.closeMaterialDropdowns();
    this.closePrimeDropdowns();
  }

  private isInsideDropdownPanel(el: Element): boolean {
    return !!(
      el.closest('.mat-mdc-select-panel') ||
      el.closest('.mat-mdc-autocomplete-panel') ||
      el.closest('.mat-autocomplete-panel') ||
      el.closest('.p-dropdown-panel') ||
      el.closest('.p-multiselect-panel') ||
      el.closest('.p-autocomplete-panel')
    );
  }

  private closeMaterialDropdowns(): void {
    const hasSelect = !!this.document.querySelector(
      '.cdk-overlay-pane .mat-mdc-select-panel, .cdk-overlay-pane .mat-select-panel',
    );
    const hasAutocomplete = !!this.document.querySelector(
      '.cdk-overlay-pane .mat-mdc-autocomplete-panel, .cdk-overlay-pane .mat-autocomplete-panel',
    );
    if (!hasSelect && !hasAutocomplete) {
      return;
    }

    // Prefer backdrop click — never send Escape while a dialog is open
    const backdrop = this.document.querySelector(
      '.cdk-overlay-backdrop.cdk-overlay-transparent-backdrop',
    ) as HTMLElement | null;
    if (backdrop) {
      backdrop.click();
      return;
    }

    const dialogOpen = !!this.document.querySelector(
      '.mat-mdc-dialog-container, .mat-dialog-container',
    );
    if (!dialogOpen) {
      this.document.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
      );
    }
  }

  private closePrimeDropdowns(): void {
    const openPanels = this.document.querySelectorAll(
      '.p-dropdown-panel, .p-multiselect-panel, .p-autocomplete-panel',
    );
    if (!openPanels.length) {
      return;
    }
    this.document.dispatchEvent(
      new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }),
    );
  }
}
